import React, { useState, useEffect, useCallback } from 'react';
import { Session } from '@supabase/supabase-js';
import { UserProfile, FinancialConfig, Quote, Material, AppView, ProfileStep } from '../types';
import { CATEGORIES } from '../constants/categories';

export interface UseAppNavigationProps {
  session: Session | null;
  user: UserProfile | null;
  config: FinancialConfig | null;
  materials: Material[];
}

export interface UseAppNavigationReturn {
  view: AppView;
  setView: React.Dispatch<React.SetStateAction<AppView>>;
  profileStep: ProfileStep | undefined;
  setProfileStep: React.Dispatch<React.SetStateAction<ProfileStep | undefined>>;
  initialQuoteDraft: {
    materialCost?: number;
    timeSpent?: number;
    serviceName?: string;
    serviceDescription?: string;
    clientName?: string;
    clientPhone?: string;
    clientAddress?: string;
  } | null;
  setInitialQuoteDraft: React.Dispatch<React.SetStateAction<{
    materialCost?: number;
    timeSpent?: number;
    serviceName?: string;
    serviceDescription?: string;
    clientName?: string;
    clientPhone?: string;
    clientAddress?: string;
  } | null>>;
  navigateTo: (newView: AppView, step?: ProfileStep) => void;
  handleGoBack: () => void;
  handleReuseQuote: (quote: Quote) => void;
}

export function useAppNavigation({
  session,
  user,
  config,
  materials
}: UseAppNavigationProps): UseAppNavigationReturn {
  const [view, setView] = useState<AppView>(() => {
    try {
      const p = new URLSearchParams(window.location.search).get('view');
      // Respeita deep linking legítimo (ex: ?view=work-orders, ?view=calendar), mas nunca trava em 'chat' no boot
      if (p && p !== 'chat') return p as AppView;
    } catch {}

    const isEnterprise = Boolean(
      user?.isMasterUser ||
      ['Empresa', 'Empresarial', 'Enterprise', 'empresa', 'enterprise'].includes(user?.plan || '')
    );
    const isSpecialist = Boolean(
      user?.plan === 'Especialista' || 
      (user?.plan as string) === 'Premium' || 
      user?.isTrialActive
    );
    const isPro = Boolean(user?.plan === 'Pro');

    if (isEnterprise || isSpecialist) return 'erp-hub';
    return 'home';
  });
  const [profileStep, setProfileStep] = useState<ProfileStep | undefined>(undefined);
  const [initialQuoteDraft, setInitialQuoteDraft] = useState<{
    materialCost?: number;
    timeSpent?: number;
    serviceName?: string;
    serviceDescription?: string;
    clientName?: string;
    clientPhone?: string;
    clientAddress?: string;
  } | null>(null);

  const navigateTo = useCallback((newView: AppView, step?: ProfileStep) => {
    const protectedViews: AppView[] = ['dashboard', 'comparison', 'history', 'profile', 'costs'];
    if (protectedViews.includes(newView) && (!user || user.id === 'guest')) {
      const pendingEmail = localStorage.getItem('rmax_pending_magic_email');
      let promptMessage = 'Essa área é de acesso exclusivo para usuários cadastrados. Deseja receber um link de acesso instantâneo para destravar todos os módulos?';
      
      if (pendingEmail) {
        promptMessage = `Já enviamos um link de acesso para ${pendingEmail}.\n\nBasta conferir sua caixa de entrada (ou pasta de spam) e clicar no link para destravar este módulo!\n\nDeseja ir para a tela de acesso?`;
      }

      if (window.confirm(promptMessage)) {
        window.location.href = '/auth';
      }
      return;
    }
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('view', newView);
      window.history.pushState({ view: newView, profileStep: step }, '', url.toString());
    } catch {
      window.history.pushState({ view: newView, profileStep: step }, '');
    }
    if (step) setProfileStep(step);
    setView(newView);
  }, [user]);

  const isSpecialistOrPremium = Boolean(
    user?.plan === 'Especialista' || 
    (user?.plan as string) === 'Premium' || 
    user?.isTrialActive
  );

  const handleGoBack = useCallback(() => {
    const isEnterpriseOrSpecialist = Boolean(
      user?.isMasterUser ||
      ['Empresa', 'Empresarial', 'Enterprise', 'empresa', 'enterprise'].includes(user?.plan || '') ||
      user?.plan === 'Especialista' || 
      (user?.plan as string) === 'Premium' || 
      user?.isTrialActive
    );
    const targetHomeView: AppView = isEnterpriseOrSpecialist 
      ? 'erp-hub' 
      : 'home';

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('view', targetHomeView);
      window.history.pushState({ ...(window.history.state || {}), view: targetHomeView }, '', url.toString());
    } catch {
      window.history.pushState({ ...(window.history.state || {}), view: targetHomeView }, '', '');
    }
    setView(targetHomeView);
  }, [user]);

  useEffect(() => {
    const rawUrlView = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('view') : null;
    const hasExplicitViewInUrl = Boolean(rawUrlView && rawUrlView !== 'chat');
    if (hasExplicitViewInUrl) return;

    const isEnterprise = Boolean(
      user?.isMasterUser ||
      ['Empresa', 'Empresarial', 'Enterprise', 'empresa', 'enterprise'].includes(user?.plan || '')
    );
    const isSpecialist = Boolean(
      user?.plan === 'Especialista' || 
      (user?.plan as string) === 'Premium' || 
      user?.isTrialActive
    );

    // Se o usuário é Empresa ou Especialista e está em 'home', move para seu Hub 'erp-hub'
    if ((isEnterprise || isSpecialist) && view === 'home') {
      setView('erp-hub');
    }
  }, [user?.plan, user?.isMasterUser, user?.isTrialActive, view]);

  const handleReuseQuote = useCallback((quote: Quote) => {
    if (!user) return;

    // A) Extrair dados úteis e garantir absoluta segurança de tipos
    const originalProducts = Array.isArray(quote.products) ? quote.products : [];
    const originalServices = Array.isArray(quote.services) ? quote.services : [];

    const cleanProducts = originalProducts.map((p) => {
      return {
        name: p.name || '',
        quantity: p.quantity || 1,
        unitValue: p.unitValue || 0,
        total: p.total || 0,
        materialId: p.materialId,
        isMarkupHidden: p.isMarkupHidden ?? false,
        isFromRecipe: p.isFromRecipe ?? false,
        showOnPdf: p.showOnPdf ?? true,
      };
    });

    const cleanServices = originalServices.map((s) => ({
      name: s.name || '',
      value: s.value || 0,
    }));

    const matchedCategory = CATEGORIES.find(c => c.name === quote.category);
    const categoryIndex = matchedCategory ? matchedCategory.id : 1;

    // B) Injetar os dados no estado global/contexto ativo do gerador
    // C) Limpar atributos únicos (ID novo, rascunho pendente)
    const draftData = {
      clientName: quote.clientName || '',
      clientPhone: quote.clientPhone || '',
      clientAddress: quote.clientAddress || '',
      serviceName: quote.rawServiceName || quote.serviceName || '',
      serviceDescription: quote.serviceDescription || quote.service_description || '',
      categoryIndex: categoryIndex,
      materialCost: cleanProducts.length > 0 ? cleanProducts.reduce((acc, p) => acc + (p.total || 0), 0) : (quote.materialCost || 0),
      timeHours: Math.floor((quote.timeSpentMinutes || 0) / 60),
      timeMinutes: (quote.timeSpentMinutes || 0) % 60,
      quantity: 1,
      extraCosts: 0,
      deadline: '',
      phone: user.phone || '',
      profitMarginInput: (quote.appliedProfitMargin || 0) * 100,
      products: cleanProducts,
      services: cleanServices,
    };

    const initialMessages = [{ 
      id: Date.now().toString(), 
      role: 'assistant' as const, 
      content: "Orçamento carregado! O que você quer alterar, incluir, ou deseja mudar o nome do cliente?", 
      type: 'text' as const
    }];

    localStorage.setItem(`rmax_draft_form_${user.id}`, JSON.stringify(draftData));
    localStorage.setItem(`rmax_draft_messages_${user.id}`, JSON.stringify(initialMessages));
    localStorage.setItem(`rmax_draft_ready_${user.id}`, 'true');

    // D) Redirecionar imediatamente para a tela principal do Assistente (chat)
    setView('chat');
  }, [user]);

  const sessionRef = React.useRef(session);
  const userRef = React.useRef(user);
  const configRef = React.useRef(config);

  React.useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  React.useEffect(() => {
    userRef.current = user;
  }, [user]);

  React.useEffect(() => {
    configRef.current = config;
  }, [config]);

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const currentSession = sessionRef.current;
      const currentUser = userRef.current;
      const currentConfig = configRef.current;
      const isSpecialist = Boolean(
        currentUser?.plan === 'Especialista' || 
        (currentUser?.plan as string) === 'Premium' || 
        currentUser?.isTrialActive
      );
      const defaultAuthenticatedView: AppView = isSpecialist ? 'erp-hub' : 'home';
      let targetView = event.state?.view;
      if (!targetView) {
        try {
          const urlView = new URLSearchParams(window.location.search).get('view');
          if (urlView && urlView !== 'chat') targetView = urlView as AppView;
        } catch {}
      }
      if (!targetView) {
        targetView = (currentSession && currentUser && currentConfig ? defaultAuthenticatedView : 'reverse-onboarding');
      }
      
      const protectedViews: AppView[] = ['dashboard', 'comparison', 'history', 'profile', 'costs', 'erp-hub'];
      if (protectedViews.includes(targetView) && (!currentUser || currentUser.id === 'guest')) {
        setView('home');
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('view', 'home');
          window.history.replaceState({ view: 'home' }, '', url.toString());
        } catch {
          window.history.replaceState({ view: 'home' }, '', '');
        }
        return;
      }

      if (event.state?.profileStep) setProfileStep(event.state.profileStep);
      setView(targetView);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get('view') !== view) {
        url.searchParams.set('view', view);
        window.history.replaceState({ ...(window.history.state || {}), view }, '', url.toString());
      } else if (!window.history.state || !('view' in window.history.state)) {
        window.history.replaceState({ ...(window.history.state || {}), view }, '', url.toString());
      }
    } catch {
      if (!window.history.state || !('view' in window.history.state)) {
        window.history.replaceState({ ...(window.history.state || {}), view }, '', '');
      }
    }
  }, [view]);

  return {
    view,
    setView,
    profileStep,
    setProfileStep,
    initialQuoteDraft,
    setInitialQuoteDraft,
    navigateTo,
    handleGoBack,
    handleReuseQuote
  };
}
